import { Balance, Expense, ParticipantBreakdown, Settlement, SettlementStrategy, ShareRatios, SponsorExpense } from "../types";
import i18n from "@/i18n/i18n.ts";

// The unit is stated once in the event header, so amounts themselves carry only
// the "K" suffix — repeating "₫" on every figure was noise on a bill made almost
// entirely of numbers. An amount too small to round to a K has no suffix to show,
// so it falls back to the full ₫ form rather than reading as a bare "0K".
export const formatCurrency = (amount: number, compact?: boolean): string => {
    if (compact) {
        const kValue = Math.ceil(amount / 1000);
        if (kValue !== 0) {
            return `${new Intl.NumberFormat('vi-VN').format(kValue)}K`;
        }
    }
    return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND'
    }).format(amount);
};

export const formatCurrencyReadable = (amount: number, locale?: string, compact?: boolean): string => {
    const displayAmount = compact ? amount / 1000 : amount;
    const currentLocale = locale || i18n.language || 'en';
    const absAmount = Math.abs(displayAmount);

    if (absAmount === 0) return '';

    const isVi = currentLocale.startsWith('vi');

    if (isVi) {
        const units: [number, string][] = [
            [1_000_000_000, 'tỷ'],
            [1_000_000, 'triệu'],
            [1_000, 'ngàn'],
        ];
        for (const [divisor, unit] of units) {
            if (absAmount >= divisor) {
                const val = absAmount / divisor;
                const formatted = val % 1 === 0 ? val.toString() : val.toFixed(1).replace(/\.0$/, '');
                return `${formatted} ${unit} đồng`;
            }
        }
        return `${absAmount} đồng`;
    } else {
        const units: [number, string][] = [
            [1_000_000_000, 'billion'],
            [1_000_000, 'million'],
            [1_000, 'thousand'],
        ];
        for (const [divisor, unit] of units) {
            if (absAmount >= divisor) {
                const val = absAmount / divisor;
                const formatted = val % 1 === 0 ? val.toString() : val.toFixed(1).replace(/\.0$/, '');
                return `${formatted} ${unit} VND`;
            }
        }
        return `${absAmount} VND`;
    }
};

// Bills read participant names from `events/<id>/meta/participants`, but Firebase
// only lets the event owner write that node. Anyone else renaming themselves can
// reach `eventUsers` alone — so that name has to win here, otherwise a rename by
// a plain participant would silently show up nowhere.
export const resolveParticipantNames = (
    metaParticipants: { [userId: string]: string } | string[] | undefined,
    eventUsers: { [userId: string]: { displayName?: string } } | undefined
): { [userId: string]: string } => {
    const names: { [userId: string]: string } = {};

    if (Array.isArray(metaParticipants)) {
        // Legacy events stored only the ids here; the names lived in eventUsers
        metaParticipants.forEach(id => { names[id] = id; });
    } else if (metaParticipants) {
        Object.assign(names, metaParticipants);
    }

    Object.entries(eventUsers || {}).forEach(([id, eventUser]) => {
        if (eventUser?.displayName && id in names) {
            names[id] = eventUser.displayName;
        }
    });

    return names;
};

export const generateId = (): string => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

// Type guard to check if an expense is a sponsor expense
const isSponsorExpense = (expense: Expense | SponsorExpense): expense is SponsorExpense => {
    return expense.type === 'sponsor';
};

// --- Share ratios ---
// The event owner can set someone to carry less than a full share (a child at
// 50%) or more (a couple at 200%). Every split — expenses and sponsorships alike —
// is then divided by those weights instead of by head count, so a 50% person
// always pays exactly half of what a 100% person in the same split pays, and the
// part they do not carry lands on everyone else in proportion.
export const DEFAULT_SHARE_RATIO = 100;
export const MAX_SHARE_RATIO = 1000;

export const getShareRatio = (shareRatios: ShareRatios | undefined, participantId: string): number => {
    const ratio = shareRatios?.[participantId];
    return typeof ratio === 'number' && Number.isFinite(ratio) && ratio >= 0 ? ratio : DEFAULT_SHARE_RATIO;
};

// Each participant's part of an amount. A split where everyone carries the same
// ratio stays a plain division — which keeps events without ratios on exactly the
// figures they had before — and that includes a split of nobody but 0% people:
// a bill put on them still has to be paid by someone.
export const splitByRatio = (
    amount: number,
    participants: string[],
    shareRatios?: ShareRatios
): Record<string, number> => {
    const ratios = participants.map(participant => getShareRatio(shareRatios, participant));
    const totalRatio = ratios.reduce((sum, ratio) => sum + ratio, 0);
    const isEven = ratios.every(ratio => ratio === ratios[0]);

    const shares: Record<string, number> = {};
    participants.forEach((participant, index) => {
        const share = isEven
            ? amount / participants.length
            : amount * ratios[index] / totalRatio;
        shares[participant] = (shares[participant] || 0) + share;
    });
    return shares;
};

// How many full shares a split adds up to: the head count, until someone in it
// carries more or less than 100%. Share = amount ÷ this × the person's ratio.
export const getSplitUnits = (participants: string[], shareRatios?: ShareRatios): number => {
    const totalRatio = participants.reduce((sum, participant) => sum + getShareRatio(shareRatios, participant), 0);
    // Nobody but 0% people — splitByRatio splits that evenly
    return totalRatio > 0 ? totalRatio / DEFAULT_SHARE_RATIO : participants.length;
};

// What each person in a split pays, for a "… each" label: one figure while the
// split is even, the lowest to the highest once ratios make it uneven — a single
// number would then be wrong for somebody.
export const formatShareRange = (
    amount: number,
    participants: string[],
    shareRatios: ShareRatios | undefined,
    format: (value: number) => string
): string => {
    const shares = Object.values(splitByRatio(amount, participants, shareRatios));
    const low = format(Math.min(...shares));
    const high = format(Math.max(...shares));
    return low === high ? low : `${low}–${high}`;
};

// An external sponsor whose cash nobody was recorded as holding still has to
// reach whoever fronted the bills. That money is real, but it sits outside the
// participant list, so it gets a pseudo-participant of its own: without one the
// sponsorship simply vanished from every settlement plan, and the group was
// asked to cover money it had already been given.
//
// The prefix cannot collide with a Firebase uid or an anonymous `~u_` id.
export const SPONSOR_FUND_PREFIX = '__sponsorFund:';

export const sponsorFundId = (expenseId: string): string => `${SPONSOR_FUND_PREFIX}${expenseId}`;

export const isSponsorFundId = (id: string): boolean => id.startsWith(SPONSOR_FUND_PREFIX);

export interface SponsorFund {
    id: string;         // pseudo-participant id used in Balance / Settlement
    expenseId: string;
    name: string;       // the sponsor's name, for display
    amount: number;     // positive
}

// Sponsorships that are external (no participant sponsor) *and* unheld (no
// participant holding the cash) — the ones that need a fund of their own.
export const getSponsorFunds = (expenses: (Expense | SponsorExpense)[]): SponsorFund[] => {
    const funds: SponsorFund[] = [];
    expenses.forEach(expense => {
        if (!isSponsorExpense(expense)) return;
        if (expense.sponsorHolder || expense.sponsorPayer) return;
        funds.push({
            id: sponsorFundId(expense.id),
            expenseId: expense.id,
            name: expense.sponsorName || expense.description,
            amount: Math.abs(expense.amount),
        });
    });
    return funds;
};

// Book an unheld external sponsorship: every beneficiary owes that much less,
// and the fund owes the group the amount it promised. Keeps the ledger at zero,
// so the settlement plan can show who the sponsor cash has to be handed to.
const applyUnheldSponsors = (
    balances: Balance,
    expenses: (Expense | SponsorExpense)[],
    shareRatios?: ShareRatios
): void => {
    expenses.forEach(expense => {
        if (!isSponsorExpense(expense)) return;
        if (expense.sponsorHolder || expense.sponsorPayer) return;
        if (!expense.splitBetween || expense.splitBetween.length === 0) {
            console.warn('Sponsor expense has no beneficiaries:', expense.id);
            return;
        }

        const sponsorAmount = Math.abs(expense.amount);
        const fundId = sponsorFundId(expense.id);

        balances[fundId] = (balances[fundId] || 0) - sponsorAmount;
        Object.entries(splitByRatio(sponsorAmount, expense.splitBetween, shareRatios)).forEach(([participant, share]) => {
            balances[participant] = (balances[participant] || 0) + share;
        });
    });
};

// Per-person share of regular (non-sponsor) expenses — needed by coverFirst sponsorships
const calculateRegularShares = (
    expenses: (Expense | SponsorExpense)[],
    shareRatios?: ShareRatios
): Record<string, number> => {
    const shares: Record<string, number> = {};
    expenses.forEach(expense => {
        if (isSponsorExpense(expense)) return;
        if (!expense.splitBetween || expense.splitBetween.length === 0) return;
        Object.entries(splitByRatio(expense.amount, expense.splitBetween, shareRatios)).forEach(([participant, share]) => {
            shares[participant] = (shares[participant] || 0) + share;
        });
    });
    return shares;
};

// Apply participant-sponsor expenses (sponsorPayer) to balances.
// - payShare: the sponsorship is an extra gift on top of the sponsor's own expense share;
//   every beneficiary (sponsor included) gets an equal cost reduction.
// - coverFirst: the sponsorship pays the sponsor's own share of regular expenses first;
//   only the surplus is split among the OTHER beneficiaries, so the sponsor never pays
//   more than the sponsorship amount itself.
const applyParticipantSponsors = (
    balances: Balance,
    expenses: (Expense | SponsorExpense)[],
    shareRatios?: ShareRatios
): void => {
    const regularShares = calculateRegularShares(expenses, shareRatios);
    // Track how much of each sponsor's own share has already been covered,
    // so multiple coverFirst sponsorships from the same person don't double-cover it
    const coveredSoFar: Record<string, number> = {};

    expenses.forEach(expense => {
        if (!isSponsorExpense(expense) || !expense.sponsorPayer) return;
        if (!expense.splitBetween || expense.splitBetween.length === 0) {
            console.warn('Sponsor expense has no beneficiaries:', expense.id);
            return;
        }

        const sponsorPayer = expense.sponsorPayer;
        const sponsorAmount = Math.abs(expense.amount);
        const sponsorMode = expense.participantSponsorMode || 'payShare';

        if (sponsorMode === 'coverFirst') {
            const ownShare = regularShares[sponsorPayer] || 0;
            const alreadyCovered = coveredSoFar[sponsorPayer] || 0;
            const covered = Math.min(sponsorAmount, Math.max(0, ownShare - alreadyCovered));
            coveredSoFar[sponsorPayer] = alreadyCovered + covered;

            const surplus = sponsorAmount - covered;
            const otherBeneficiaries = expense.splitBetween.filter(p => p !== sponsorPayer);
            if (surplus > 0 && otherBeneficiaries.length > 0) {
                balances[sponsorPayer] -= surplus;
                Object.entries(splitByRatio(surplus, otherBeneficiaries, shareRatios)).forEach(([participant, share]) => {
                    balances[participant] += share;
                });
            }
        } else {
            // payShare: sponsor pays the full amount as a gift on top of their own share
            balances[sponsorPayer] -= sponsorAmount;
            Object.entries(splitByRatio(sponsorAmount, expense.splitBetween, shareRatios)).forEach(([participant, share]) => {
                balances[participant] += share;
            });
        }
    });
};

export const calculateBalances = (
    expenses: (Expense | SponsorExpense)[],
    participantIds: string[],
    shareRatios?: ShareRatios
): Balance => {
    const balances: Balance = {};

    // Initialize balances
    participantIds.forEach(participantId => {
        balances[participantId] = 0;
    });

    // Calculate balances from expenses
    expenses.forEach(expense => {
        const { payer, amount, splitBetween } = expense;

        // Guard against division by zero
        if (!splitBetween || splitBetween.length === 0) {
            console.warn('Expense has no participants to split between:', expense.id);
            return;
        }

        const shares = splitByRatio(amount, splitBetween, shareRatios);

        if (isSponsorExpense(expense)) {
            // Participant sponsors (sponsorPayer) are handled by applyParticipantSponsors below,
            // because coverFirst needs everyone's regular shares computed first
            if (!expense.sponsorPayer) {
                // External sponsor (direct or with holder) - every beneficiary benefits by their share
                Object.entries(shares).forEach(([participant, share]) => {
                    balances[participant] -= share; // share is negative, so this adds
                });
            }
        } else {
            // Regular expense
            // Add what the payer paid
            balances[payer] += amount;

            // Subtract what each person owes
            Object.entries(shares).forEach(([participant, share]) => {
                balances[participant] -= share;
            });
        }
    });

    applyParticipantSponsors(balances, expenses, shareRatios);

    return balances;
};

// Function for when sponsor surplus should be distributed to members
export const calculateBalancesForSettlements = (
    expenses: (Expense | SponsorExpense)[],
    participantIds: string[],
    shareRatios?: ShareRatios
): Balance => {
    const balances: Balance = {};

    // Initialize balances
    participantIds.forEach(participantId => {
        balances[participantId] = 0;
    });

    // Calculate ONLY regular expenses first (ignore sponsor expenses)
    expenses.forEach(expense => {
        if (!isSponsorExpense(expense)) {
            const { payer, amount, splitBetween } = expense;

            // Guard against division by zero
            if (!splitBetween || splitBetween.length === 0) {
                console.warn('Expense has no participants to split between:', expense.id);
                return;
            }

            // Add what the payer paid
            balances[payer] += amount;

            // Subtract what each person owes
            Object.entries(splitByRatio(amount, splitBetween, shareRatios)).forEach(([participant, share]) => {
                balances[participant] -= share;
            });
        }
    });

    // Now handle sponsor holder obligations
    expenses.forEach(expense => {
        if (isSponsorExpense(expense) && expense.sponsorHolder) {
            const sponsorAmount = Math.abs(expense.amount);

            // Guard against division by zero
            if (!expense.splitBetween || expense.splitBetween.length === 0) {
                console.warn('Sponsor expense has no beneficiaries:', expense.id);
                return;
            }

            // Each beneficiary should receive their sponsor share
            // But we implement this by making sponsor holder owe them money
            Object.entries(splitByRatio(sponsorAmount, expense.splitBetween, shareRatios)).forEach(([participant, share]) => {
                if (participant !== expense.sponsorHolder) {
                    // Sponsor holder owes this participant their sponsor share
                    balances[expense.sponsorHolder!] -= share;
                    balances[participant] += share;
                }
                // If sponsor holder is also a beneficiary, no adjustment needed:
                // they simply keep their own share from the sponsor fund they're holding
            });
        }

    });

    // External sponsors nobody holds — booked against their own fund
    applyUnheldSponsors(balances, expenses, shareRatios);

    // Participant sponsors (payShare / coverFirst) — shared logic
    applyParticipantSponsors(balances, expenses, shareRatios);

    return balances;
};

// Function for when sponsor surplus should NOT be distributed (return to fund)
// Uses sponsor money first, then splits remaining costs
export const calculateBalancesWithSponsorReimbursement = (
    expenses: (Expense | SponsorExpense)[],
    participantIds: string[],
    shareRatios?: ShareRatios
): Balance => {
    const balances: Balance = {};

    // Initialize balances
    participantIds.forEach(participantId => {
        balances[participantId] = 0;
    });

    // Every source of sponsor cash: a participant holding it, or — when nobody was
    // recorded as holding it — the sponsorship's own fund. Unheld sponsorships used
    // to be left out of the pool entirely, so their money never covered anything.
    const sponsorSources: Array<{ id: string; amount: number }> = [];

    expenses.forEach(expense => {
        if (!isSponsorExpense(expense)) return;
        if (expense.sponsorPayer) return; // handled by applyParticipantSponsors

        const sponsorAmount = Math.abs(expense.amount); // sponsor amount is negative
        sponsorSources.push({
            id: expense.sponsorHolder || sponsorFundId(expense.id),
            amount: sponsorAmount,
        });
    });

    const totalSponsorMoney = sponsorSources.reduce((sum, source) => sum + source.amount, 0);

    // Charge a covered cost back to the sponsor pool, split across the sources in
    // proportion to what each put in. Charging every holder the full amount (the
    // previous behaviour) counted the cost once per holder.
    const chargeSponsorPool = (coveredAmount: number) => {
        if (totalSponsorMoney <= 0) return;
        sponsorSources.forEach(source => {
            const weight = source.amount / totalSponsorMoney;
            balances[source.id] = (balances[source.id] || 0) - coveredAmount * weight;
        });
    };

    // Calculate total regular expenses
    let totalRegularExpenses = 0;
    expenses.forEach(expense => {
        if (!isSponsorExpense(expense)) {
            totalRegularExpenses += expense.amount;
        }
    });

    // Determine if sponsor money covers all expenses
    const sponsorCoversAll = totalSponsorMoney >= totalRegularExpenses;

    // Process regular expenses
    expenses.forEach(expense => {
        if (!isSponsorExpense(expense)) {
            const { payer, amount, splitBetween } = expense;

            // Guard against division by zero
            if (!splitBetween || splitBetween.length === 0) {
                console.warn('Expense has no participants to split between:', expense.id);
                return;
            }

            if (sponsorCoversAll) {
                // Case 1: Sponsor money covers everything
                // The sponsor pool pays for everything, people don't owe anything
                chargeSponsorPool(amount);
                balances[payer] += amount; // Payer gets reimbursed
            } else {
                // Case 2: Sponsor money doesn't cover everything
                // Calculate how much of this expense is covered by sponsor
                const sponsorCoverageRatio = totalSponsorMoney / totalRegularExpenses;
                const sponsorCoveredAmount = amount * sponsorCoverageRatio;
                const memberCoveredAmount = amount - sponsorCoveredAmount;

                // The sponsor pool pays the sponsor-covered portion
                chargeSponsorPool(sponsorCoveredAmount);
                balances[payer] += sponsorCoveredAmount; // Payer gets reimbursed for sponsor portion

                // For the remaining portion, payer keeps what they paid
                balances[payer] += memberCoveredAmount;

                // Everyone (including payer) owes their share of the member-covered portion
                Object.entries(splitByRatio(memberCoveredAmount, splitBetween, shareRatios)).forEach(([participant, share]) => {
                    balances[participant] -= share;
                });
            }
        }
    });

    // Participant sponsors (payShare / coverFirst) — shared logic
    applyParticipantSponsors(balances, expenses, shareRatios);

    return balances;
};

// Enhanced function to get sponsor coverage information
export const getSponsorCoverageInfo = (expenses: (Expense | SponsorExpense)[]): {
    totalSponsorMoney: number;
    totalRegularExpenses: number;
    sponsorCoversAll: boolean;
    shortfall: number;
    surplus: number;
    coveragePercentage: number;
} => {
    let totalSponsorMoney = 0;
    let totalRegularExpenses = 0;

    expenses.forEach(expense => {
        if (isSponsorExpense(expense)) {
            totalSponsorMoney += Math.abs(expense.amount);
        } else {
            totalRegularExpenses += expense.amount;
        }
    });

    const sponsorCoversAll = totalSponsorMoney >= totalRegularExpenses;
    const shortfall = Math.max(0, totalRegularExpenses - totalSponsorMoney);
    const surplus = Math.max(0, totalSponsorMoney - totalRegularExpenses);
    const coveragePercentage = totalRegularExpenses > 0 ? (totalSponsorMoney / totalRegularExpenses) * 100 : 100;

    return {
        totalSponsorMoney,
        totalRegularExpenses,
        sponsorCoversAll,
        shortfall,
        surplus,
        coveragePercentage: Math.min(100, coveragePercentage)
    };
};

// Calculate sponsor amounts only for sponsor holders
export const calculateSponsorAmounts = (expenses: (Expense | SponsorExpense)[], participantIds: string[]): Record<string, number> => {
    const sponsorAmounts: Record<string, number> = {};

    // Initialize sponsor amounts
    participantIds.forEach(participantId => {
        sponsorAmounts[participantId] = 0;
    });

    // Calculate sponsor amounts only for sponsor holders
    expenses.forEach(expense => {
        if (isSponsorExpense(expense) && expense.sponsorHolder) {
            const { amount } = expense;
            const sponsorAmount = Math.abs(amount); // Use abs because sponsor amount is negative

            // Only add sponsor amount to the sponsor holder
            sponsorAmounts[expense.sponsorHolder] += sponsorAmount;
        }
    });

    return sponsorAmounts;
};

// --- Settlement Strategy: Single Collector ---
// All debtors pay to one designated collector, who then distributes to creditors.
export const getSettlementsSingleCollector = (balances: Balance, collectorId: string): Settlement[] => {
    const settlements: Settlement[] = [];

    Object.entries(balances).forEach(([person, balance]) => {
        if (person === collectorId) return; // skip the collector

        if (balance < -0.01) {
            // Debtor pays the collector
            settlements.push({
                from: person,
                to: collectorId,
                amount: Math.round(Math.abs(balance)),
                fromName: person,
                toName: collectorId
            });
        } else if (balance > 0.01) {
            // Collector pays creditor
            settlements.push({
                from: collectorId,
                to: person,
                amount: Math.round(balance),
                fromName: collectorId,
                toName: person
            });
        }
    });

    return settlements;
};

// --- Settlement Strategy: Simple Pay (each debtor pays only ONE person) ---
// Assigns each debtor to exactly one creditor. May result in slightly more
// total transactions (creditor-to-creditor transfers) but every debtor only
// needs to make a single payment — simpler for participants.
export const getSettlementsSimplePay = (balances: Balance): Settlement[] => {
    const settlements: Settlement[] = [];
    const creditors: Array<{ person: string; amount: number }> = [];
    const debtors: Array<{ person: string; amount: number }> = [];

    Object.entries(balances).forEach(([person, balance]) => {
        if (balance > 0.01) {
            creditors.push({ person, amount: balance });
        } else if (balance < -0.01) {
            debtors.push({ person, amount: -balance }); // store as positive
        }
    });

    if (creditors.length === 0 || debtors.length === 0) return [];

    // Sort creditors largest first so we fill them up
    creditors.sort((a, b) => b.amount - a.amount);
    // Sort debtors largest first for better packing
    debtors.sort((a, b) => b.amount - a.amount);

    // Working copies of how much each creditor still needs to receive
    const credRemaining = creditors.map(c => ({ ...c }));

    // Assign each debtor to one creditor (the one with most remaining capacity)
    for (const debtor of debtors) {
        // Find creditor with most remaining amount
        credRemaining.sort((a, b) => b.amount - a.amount);
        const target = credRemaining[0];

        if (!target || target.amount < 0.01) continue;

        settlements.push({
            from: debtor.person,
            to: target.person,
            amount: Math.round(debtor.amount),
            fromName: debtor.person,
            toName: target.person
        });

        target.amount -= debtor.amount;
    }

    // Now some creditors may have received too much and others too little.
    // Add creditor-to-creditor transfers to balance.
    const overPaid = credRemaining.filter(c => c.amount < -0.01)
        .map(c => ({ person: c.person, amount: -c.amount })); // amount they need to send
    const underPaid = credRemaining.filter(c => c.amount > 0.01);

    overPaid.sort((a, b) => b.amount - a.amount);
    underPaid.sort((a, b) => b.amount - a.amount);

    let oi = 0, ui = 0;
    while (oi < overPaid.length && ui < underPaid.length) {
        const amount = Math.min(overPaid[oi].amount, underPaid[ui].amount);
        if (amount > 0.01) {
            settlements.push({
                from: overPaid[oi].person,
                to: underPaid[ui].person,
                amount: Math.round(amount),
                fromName: overPaid[oi].person,
                toName: underPaid[ui].person
            });
        }
        overPaid[oi].amount -= amount;
        underPaid[ui].amount -= amount;
        if (overPaid[oi].amount < 0.01) oi++;
        if (underPaid[ui].amount < 0.01) ui++;
    }

    return settlements;
};

// --- Strategy Wrapper ---
export const getSettlementsByStrategy = (
    balances: Balance,
    strategy: SettlementStrategy,
    collectorId?: string
): Settlement[] => {
    switch (strategy) {
        case 'singleCollector':
            return getSettlementsSingleCollector(balances, collectorId || '');
        case 'simplePay':
        default:
            return getSettlementsSimplePay(balances);
    }
};

// --- Settlement Breakdown ---
// Explains where a participant's balance comes from: every expense they were
// split into (with their share), plus every expense they paid for themselves.
// Sponsor expenses have a negative amount, so their share comes out negative —
// i.e. money taken off what the participant owes.
export const getParticipantBreakdown = (
    expenses: (Expense | SponsorExpense)[],
    participantId: string,
    shareRatios?: ShareRatios
): ParticipantBreakdown => {
    const joined: ParticipantBreakdown['joined'] = [];
    const paid: ParticipantBreakdown['paid'] = [];
    let totalOwed = 0;
    let totalPaid = 0;

    expenses.forEach(expense => {
        const sponsor = isSponsorExpense(expense);

        if (expense.splitBetween?.includes(participantId)) {
            const share = splitByRatio(expense.amount, expense.splitBetween, shareRatios)[participantId];
            joined.push({
                id: expense.id,
                description: expense.description,
                amount: expense.amount,
                splitCount: expense.splitBetween.length,
                splitUnits: getSplitUnits(expense.splitBetween, shareRatios),
                share,
                isSponsor: sponsor
            });
            totalOwed += share;
        }

        if (!sponsor && expense.payer === participantId) {
            paid.push({
                id: expense.id,
                description: expense.description,
                amount: expense.amount
            });
            totalPaid += expense.amount;
        }
    });

    return {
        joined,
        paid,
        totalOwed,
        totalPaid,
        net: totalPaid - totalOwed,
        shareRatio: getShareRatio(shareRatios, participantId)
    };
};

// Local midnight for a timestamp. Expenses carry their day inside `timestamp`,
// so this is what decides which day-group an entry belongs to.
export const startOfDay = (timestamp: number): number => {
    const date = new Date(timestamp);
    date.setHours(0, 0, 0, 0);
    return date.getTime();
};

export const formatDate = (timestamp: number, locale?: string) => {
    const currentLocale = locale || i18n.language || 'en';

    return new Date(timestamp).toLocaleDateString(currentLocale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

/**
 * Who the last expense this person entered was split between, kept to the people
 * still on the bill.
 *
 * Entering a bill is repetitive — the same subset of the group eats together all
 * evening — so the picker opens on whatever they chose last rather than on the
 * whole group every time. Returns null when they have no expense here yet, which
 * is the caller's cue to fall back to everyone.
 */
export const getLastSplitBetween = (
    expenses: (Expense | SponsorExpense)[],
    userId: string,
    participantIds: string[]
): string[] | null => {
    if (!userId || participantIds.length === 0) return null;

    const own = expenses.filter(expense =>
        !isSponsorExpense(expense) && (expense.createdBy || expense.payer) === userId
    );
    if (own.length === 0) return null;

    const latest = own.reduce((newest, expense) =>
        expense.timestamp > newest.timestamp ? expense : newest
    );

    // A split may name people who have since been removed from the event
    const stillHere = (latest.splitBetween || []).filter(id => participantIds.includes(id));
    return stillHere.length > 0 ? stillHere : null;
};
